import { useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRight, Mail, Phone } from 'lucide-react';
import { navLinks, site } from '../../data/site';
import useLockBodyScroll from '../../hooks/useLockBodyScroll';
import Logo from './Logo';
import cn from '../../lib/cn';

/** Full-screen slide-in navigation for small screens. */
export default function MobileMenu({ open, onClose }) {
  useLockBodyScroll(open);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => event.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[60] bg-ink-950/50 backdrop-blur-sm lg:hidden"
          />

          <motion.nav
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.34, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-y-0 right-0 z-[65] flex w-[86%] max-w-sm flex-col bg-white shadow-lift lg:hidden"
            aria-label="Mobile navigation"
          >
            <div className="flex items-center justify-between border-b border-ink-100 px-5 py-4">
              <Logo onClick={onClose} />
              <button
                type="button"
                onClick={onClose}
                aria-label="Close menu"
                className="grid h-10 w-10 place-items-center rounded-full text-ink-600 transition hover:bg-ink-100"
              >
                <span className="relative block h-4 w-4">
                  <span className="absolute left-0 top-1/2 h-0.5 w-4 -translate-y-1/2 rotate-45 rounded bg-current" />
                  <span className="absolute left-0 top-1/2 h-0.5 w-4 -translate-y-1/2 -rotate-45 rounded bg-current" />
                </span>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-6">
              <ul className="space-y-1">
                {navLinks.map((link, index) => (
                  <motion.li
                    key={link.to}
                    initial={{ opacity: 0, x: 24 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.08 + index * 0.045, duration: 0.35 }}
                  >
                    <NavLink
                      to={link.to}
                      end={link.to === '/'}
                      onClick={onClose}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center justify-between rounded-xl px-4 py-3.5 font-display text-base font-semibold transition',
                          isActive
                            ? 'bg-brand-50 text-brand-700'
                            : 'text-ink-800 hover:bg-ink-50'
                        )
                      }
                    >
                      {link.label}
                      <ArrowRight className="h-4 w-4 opacity-40" />
                    </NavLink>
                  </motion.li>
                ))}
              </ul>

              <div className="mt-8 space-y-3">
                <NavLink
                  to="/request-pickup"
                  onClick={onClose}
                  className="btn bg-brand-600 px-5 py-3.5 text-white shadow-soft w-full"
                >
                  Request a Pickup
                  <ArrowRight className="h-4 w-4" />
                </NavLink>
              </div>

              <div className="mt-8 space-y-3 border-t border-ink-100 pt-6 text-sm">
                <a href={`tel:${site.phone.replace(/\s/g, '')}`} className="flex items-center gap-3 text-ink-600 hover:text-brand-700">
                  <Phone className="h-4 w-4 text-brand-600" />
                  {site.phone}
                </a>
                <a href={`mailto:${site.email}`} className="flex items-center gap-3 text-ink-600 hover:text-brand-700">
                  <Mail className="h-4 w-4 text-brand-600" />
                  {site.email}
                </a>
              </div>
            </div>
          </motion.nav>
        </>
      )}
    </AnimatePresence>
  );
}
